import type { ActivityEvent, DependencySpan, TraceTelemetry } from '@/api/activity';

export interface RequestNode {
  key: string;
  label: string;
  event?: ActivityEvent;
  span?: DependencySpan;
  relation: string;
  timestamp: string;
  children: RequestNode[];
}

export const eventName = (e: ActivityEvent): string => e.kind === 'llm'
  ? `Model · ${e.agentName ?? 'Agent'} · ${e.modelName ?? 'deployment not recorded'}`
  : e.kind === 'tool' ? `Tool · ${[e.pluginName, e.methodName].filter(Boolean).join('.')}`
  : e.kind === 'consult' ? `Consult · ${e.peerAgentName ?? 'Specialist'}`
  : `Knowledge search · ${e.indexName ?? 'index not recorded'}`;

const sort = (nodes: RequestNode[]) => nodes.sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.key.localeCompare(b.key));

/** Only a recorded PromptLog foreign key establishes SQL parentage. Chronology and shared agent names do not. */
export function activityTree(events: ActivityEvent[]): RequestNode[] {
  const groups = new Map<string, ActivityEvent[]>();
  for (const e of events) {
    const id = e.correlationId?.toLowerCase() || 'not-recorded';
    groups.set(id, [...(groups.get(id) ?? []), e]);
  }
  return [...groups].map(([id, rows]) => {
    const nodes = rows.map((event, i): RequestNode => ({ key: `event:${id}:${event.kind}:${event.id}:${i}`,
      label: eventName(event), event, relation: 'Parent not recorded', timestamp: event.timestamp, children: [] }));
    const roots: RequestNode[] = [];
    for (const node of nodes) {
      const e = node.event!;
      const parents = e.kind === 'tool' && e.llmPromptLogId != null
        ? nodes.filter((n) => n.event?.kind === 'llm' && n.event.id === e.llmPromptLogId) : [];
      if (parents.length === 1 && (e.agentId == null || parents[0].event?.agentId == null || e.agentId === parents[0].event?.agentId)) {
        node.relation = 'Recorded model-call link';
        parents[0].children.push(node);
      } else {
        if (e.llmPromptLogId != null) node.relation = 'Recorded parent unavailable or ambiguous';
        roots.push(node);
      }
    }
    nodes.forEach((n) => sort(n.children));
    return { key: `request:${id}`, label: id === 'not-recorded' ? 'Request identity not recorded' : `Request · ${id}`,
      relation: 'Grouped by recorded request identity', timestamp: sort([...nodes])[0]?.timestamp ?? '', children: sort(roots) };
  }).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

/** AI SDKs may emit W3C or legacy App Insights span identities. Normalize only recognized encodings. */
const spanKey = (id?: string | null) => {
  const value = id?.trim() ?? '';
  const legacy = /^\|[a-f0-9]{32}\.([a-f0-9]{16})\.?$/i.exec(value);
  return legacy ? legacy[1].toLowerCase() : /^[a-f0-9]{16}$/i.test(value) ? value.toLowerCase() : value;
};

export function executionTree(telemetry: TraceTelemetry | null): RequestNode[] {
  const spans = [...(telemetry?.requests ?? []), ...(telemetry?.dependencies ?? [])];
  const nodes = spans.map((span, i): RequestNode => ({ key: `span:${i}`, span,
    label: span.name || span.type || 'Execution span', relation: 'Trace root', timestamp: span.timestamp, children: [] }));
  const byId = new Map<string, RequestNode[]>();
  nodes.forEach((n) => { const id = spanKey(n.span?.spanId); if (id) byId.set(id, [...(byId.get(id) ?? []), n]); });
  const parentOf = (n: RequestNode) => {
    const id = spanKey(n.span?.parentSpanId);
    const parents = byId.get(id);
    return parents?.length === 1 ? parents[0] : undefined;
  };
  const roots: RequestNode[] = [];
  for (const node of nodes) {
    let cursor: RequestNode | undefined = node;
    const seen = new Set<RequestNode>();
    while (cursor && !seen.has(cursor)) { seen.add(cursor); cursor = parentOf(cursor); }
    const parent = parentOf(node);
    const ambiguous = (byId.get(spanKey(node.span?.spanId))?.length ?? 0) > 1;
    if (cursor || ambiguous) {
      node.relation = cursor ? 'Cyclic parent records — shown separately' : 'Duplicate span identity — shown separately';
      roots.push(node);
    } else if (parent) {
      node.relation = 'Recorded span-parent link';
      parent.children.push(node);
    } else {
      node.relation = node.span?.parentSpanId ? 'Parent not captured in this result' : node.span?.spanId ? 'Trace root' : 'Span identity not recorded';
      roots.push(node);
    }
  }
  nodes.forEach((n) => sort(n.children));
  return sort(roots);
}

export function recordedWindow(nodes: RequestNode[]): number | null {
  const starts: number[] = [], ends: number[] = [];
  const visit = (n: RequestNode) => {
    if (n.span) {
      const start = Date.parse(n.timestamp);
      if (Number.isFinite(start)) { starts.push(start); ends.push(start + Math.max(0, n.span.durationMs)); }
    }
    n.children.forEach(visit);
  };
  nodes.forEach(visit);
  return starts.length ? Math.max(...ends) - Math.min(...starts) : null;
}
